/* hibikura — motion layer
 *
 * One IntersectionObserver, no dependencies. Marks sections and list items for
 * reveal, plays each one once, then stops watching it.
 *
 * The hidden state lives behind `.hibikura-motion` on <html>, which this file
 * adds. If it never runs the page is simply visible — see hibikura-motion.css.
 */

(() => {
  const root = document.documentElement;

  // Bail out entirely when the visitor asked for less motion, or when the
  // browser lacks the observer. Either way the page stays as served.
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  if (reduced?.matches || !('IntersectionObserver' in window)) return;

  // Container, then the child selector. These names are load-bearing: a typo
  // fails silently, so they are taken from the section markup, not guessed.
  //
  // Reveals attach to element-sized blocks, never to a whole section. A
  // `.sasane` section is routinely several viewports tall, so a reveal on it
  // fires the moment its top edge appears and finishes far above where the
  // reader is looking — the animation runs, off screen, and reads as nothing
  // having happened. The blocks inside a section are viewport-sized, so their
  // reveal lands where it can actually be seen.
  //
  // Order matters: the generic containers come first and the specific lists
  // after, because a list is itself a child of a generic container and the
  // specific pass clears the reveal the generic pass put on it. Otherwise the
  // list would fade as a block while its items also staggered inside it.
  const STAGGER_GROUPS = [
    ['.sasane__inner', ':scope > *'],
    ['.hibikura-container', ':scope > *'],
    ['.sasane-trust__list', ':scope > .sasane-trust__item'],
    ['.hibikura-value-strip__list', ':scope > .hibikura-value-strip__item'],
    ['.sasane-bar', ':scope > .sasane-bar__item'],
    ['.hibikura-pillars__grid', ':scope > .hibikura-pillar'],
    ['.hibikura-products__grid', ':scope > .hibikura-product-card'],
  ];

  const MAX_STAGGER = 6; // beyond this the tail feels like waiting, not rhythm

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        el.classList.add('hk-in');
        observer.unobserve(el);
        // Release the compositor hint once the transition has finished.
        el.addEventListener('transitionend', () => el.classList.add('hk-done'), { once: true });
      }
    },
    // Trigger once the element's top has come 15% into the viewport, so the
    // movement plays where the reader is looking. threshold 0 rather than a
    // ratio: a ratio behaves differently for a short block than a tall one, and
    // these targets vary in height.
    { rootMargin: '0px 0px -15% 0px', threshold: 0 }
  );

  // Marking and observing are two phases. Marking sets opacity to 0; observing
  // can immediately set it back to 1 for anything already on screen. If both
  // happen in one go the browser never paints the hidden state, so there is no
  // "before" to transition from and the reveal is skipped — which is why
  // above-the-fold content used to appear with no animation at all. Collect the
  // targets first, let one frame paint them hidden, then start observing.
  const pending = [];

  const watch = (el) => {
    if (!el || el.hasAttribute('data-hk-reveal')) return;
    el.setAttribute('data-hk-reveal', '');
    pending.push(el);
  };

  const unwatch = (el) => {
    el.removeAttribute('data-hk-reveal');
    observer.unobserve(el);
    const i = pending.indexOf(el);
    if (i !== -1) pending.splice(i, 1);
  };

  const collect = () => {
    for (const [container, child] of STAGGER_GROUPS) {
      for (const list of document.querySelectorAll(container)) {
        // The list animates through its children, so it must not also fade as a
        // block — that would double the opacity curve.
        unwatch(list);
        const children = list.querySelectorAll(child);
        children.forEach((item, i) => {
          item.setAttribute('data-hk-stagger-item', '');
          item.style.setProperty('--hk-i', String(Math.min(i, MAX_STAGGER)));
          watch(item);
        });
      }
    }
  };

  // Hands the collected targets to the observer, one painted frame later.
  const arm = () => {
    if (pending.length === 0) return;
    const batch = pending.splice(0, pending.length);
    // Read a layout property to force the hidden state through style and layout
    // before the frame is committed.
    void document.body.offsetHeight;
    requestAnimationFrame(() => {
      for (const el of batch) observer.observe(el);
    });
  };

  root.classList.add('hibikura-motion');
  collect();
  arm();

  // Sections re-render in the theme editor and when a section loads lazily.
  document.addEventListener('shopify:section:load', () => {
    collect();
    arm();
  });

  // If the visitor turns reduced motion on mid-session, reveal everything and
  // stop observing.
  reduced?.addEventListener?.('change', (e) => {
    if (!e.matches) return;
    observer.disconnect();
    for (const el of document.querySelectorAll('[data-hk-reveal]')) el.classList.add('hk-in', 'hk-done');
  });
})();
